import { useMemo } from 'react';
import { Box, Typography, Stack, Chip, Card, CardContent, Divider, Grid } from '@mui/material';
import { CheckCircle, Error as ErrorIcon, Speed, Timer, TrendingUp } from '@mui/icons-material';
import type { ParameterizedRequest } from '@/utils/networkInterceptorParameterization';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { renderMethodOrType } from '../../utils';

type Props = {
	active: boolean;
	requests: ParameterizedRequest[];
	runResults: Record<
		string,
		{
			ok: boolean;
			status?: number;
			statusText?: string;
			durationMs: number;
			startedAt?: string;
			finishedAt?: string;
			responsePreview?: string;
			error?: string;
		}
	>;
	runExecutedAt: string | null;
	isHistoryMode: boolean;
};

const TimelineTab = ({ active, requests, runResults, runExecutedAt, isHistoryMode }: Props) => {
	// Calculate timeline data
	const timelineData = useMemo(() => {
		if (!runExecutedAt || requests.length === 0) return null;

		const requestsWithResults = requests
			.map((req) => {
				const result = runResults[req.uniqueId];
				if (!result?.startedAt) return null;

				return {
					request: req,
					result,
					startTime: new Date(result.startedAt).getTime(),
					endTime: result.finishedAt
						? new Date(result.finishedAt).getTime()
						: new Date(result.startedAt).getTime() + result.durationMs,
				};
			})
			.filter((item): item is NonNullable<typeof item> => item !== null)
			.sort((a, b) => a.startTime - b.startTime);

		if (requestsWithResults.length === 0) return null;

		const flowStartTime = requestsWithResults[0].startTime;
		const flowEndTime = Math.max(...requestsWithResults.map((r) => r.endTime));
		const totalFlowDuration = flowEndTime - flowStartTime;

		// Calculate gaps between requests
		const gaps = requestsWithResults.slice(1).map((req, idx) => {
			const prevReq = requestsWithResults[idx];
			const gapMs = req.startTime - prevReq.endTime;
			return gapMs > 0 ? gapMs : 0;
		});

		const totalGapTime = gaps.reduce((sum, gap) => sum + gap, 0);
		const totalExecutionTime = requestsWithResults.reduce(
			(sum, r) => sum + r.result.durationMs,
			0
		);

		// Find slowest request
		const slowestRequest = requestsWithResults.reduce((prev, current) =>
			current.result.durationMs > prev.result.durationMs ? current : prev
		);

		// Calculate success rate
		const successCount = requestsWithResults.filter((r) => r.result.ok).length;
		const successRate = (successCount / requestsWithResults.length) * 100;

		return {
			requestsWithResults,
			flowStartTime,
			flowEndTime,
			totalFlowDuration,
			totalGapTime,
			totalExecutionTime,
			slowestRequest,
			successRate,
			gaps,
		};
	}, [requests, runResults, runExecutedAt]);

	// Prepare ApexCharts data
	const chartData = useMemo(() => {
		if (!timelineData) return null;

		const { requestsWithResults, flowStartTime } = timelineData;

		// Create series data for the timeline chart
		const series: ApexAxisChartSeries = [
			{
				name: 'Request Duration',
				data: requestsWithResults.map((item, idx) => {
					const startOffset = item.startTime - flowStartTime;
					const endOffset = item.endTime - flowStartTime;

					return {
						x: `${idx + 1}. ${item.request.method} ${getShortUrl(
							item.request.parameterizedUrl
						)}`,
						y: [startOffset, endOffset],
						fillColor: item.result.ok ? '#4caf50' : '#f44336',
						meta: {
							duration: item.result.durationMs,
							status: item.result.status,
							statusText: item.result.statusText,
							ok: item.result.ok,
						},
					};
				}),
			},
		];

		const options: ApexOptions = {
			chart: {
				type: 'rangeBar',
				height: Math.max(400, requestsWithResults.length * 60),
				toolbar: {
					show: true,
					tools: {
						download: true,
						zoom: true,
						zoomin: true,
						zoomout: true,
						pan: true,
						reset: true,
					},
				},
			},
			plotOptions: {
				bar: {
					horizontal: true,
					distributed: false,
					rangeBarGroupRows: false,
					barHeight: '70%',
					dataLabels: {
						hideOverflowingLabels: false,
					},
				},
			},
			dataLabels: {
				enabled: true,
				// @ts-ignore
				formatter: function (_val, opts) {
					const meta = opts.w.config.series[0].data[opts.dataPointIndex].meta;
					return `${meta.duration}ms`;
				},
				style: {
					colors: ['#fff'],
					fontSize: '11px',
					fontWeight: 600,
				},
			},
			xaxis: {
				type: 'numeric',
				title: {
					text: 'Time (ms from start)',
				},
				labels: {
					formatter: (val) => `${Math.round(Number(val))}ms`,
				},
			},
			yaxis: {
				labels: {
					style: {
						fontSize: '11px',
					},
					maxWidth: 300,
				},
			},
			tooltip: {
				custom: function ({ seriesIndex, dataPointIndex, w }) {
					const data = w.config.series[seriesIndex].data[dataPointIndex];
					const meta = data.meta;
					const startTime = data.y[0];
					const endTime = data.y[1];

					return `
						<div style="padding: 12px; font-family: monospace; font-size: 12px; line-height: 1.6;">
							<div style="font-weight: bold; margin-bottom: 8px;">${data.x}</div>
							<div><strong>Start:</strong> T+${startTime}ms</div>
							<div><strong>End:</strong> T+${endTime}ms</div>
							<div><strong>Duration:</strong> ${meta.duration}ms</div>
							<div><strong>Status:</strong> ${meta.status || 'N/A'} ${
						meta.statusText ? `(${meta.statusText})` : ''
					}</div>
							<div style="color: ${meta.ok ? '#4caf50' : '#f44336'}; font-weight: bold;">
								${meta.ok ? '✓ Success' : '✗ Failed'}
							</div>
						</div>
					`;
				},
			},
			grid: {
				borderColor: '#e0e0e0',
				xaxis: {
					lines: {
						show: true,
					},
				},
			},
			legend: {
				show: false,
			},
		};

		return { series, options };
	}, [timelineData]);

	if (!active) return null;

	// Show empty state if no run results
	if (!runExecutedAt || !timelineData) {
		return (
			<Box
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					p: 4,
				}}
			>
				<Timer sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
				<Typography variant="h6" color="text.secondary" gutterBottom>
					No Timeline Data Available
				</Typography>
				<Typography variant="body2" color="text.secondary" align="center">
					{isHistoryMode
						? 'This historical run does not have timing data.'
						: 'Click RUN to execute the flow and generate timeline data.'}
				</Typography>
			</Box>
		);
	}

	const {
		requestsWithResults,
		totalFlowDuration,
		totalGapTime,
		totalExecutionTime,
		slowestRequest,
		successRate,
		gaps,
	} = timelineData;

	return (
		<Box
			sx={{
				flex: 1,
				overflow: 'auto',
				p: 3,
				height: 'calc(100vh - 100px)',
				maxHeight: 'calc(100vh - 120px)',
			}}
		>
			{/* Summary Cards */}
			<Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 15 }} sx={{ mb: 3 }}>
				<Grid item xs={12} sm={6} md={3}>
					<Card elevation={2}>
						<CardContent>
							<Stack direction="row" spacing={1} alignItems="center" mb={1}>
								<Timer color="primary" />
								<Typography variant="body2" color="text.secondary">
									Total Duration
								</Typography>
							</Stack>
							<Typography variant="h5" fontWeight="bold">
								{totalFlowDuration}ms
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{(totalFlowDuration / 1000).toFixed(2)}s
							</Typography>
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} sm={6} md={3}>
					<Card elevation={2}>
						<CardContent>
							<Stack direction="row" spacing={1} alignItems="center" mb={1}>
								<Speed color="success" />
								<Typography variant="body2" color="text.secondary">
									Execution Time
								</Typography>
							</Stack>
							<Typography variant="h5" fontWeight="bold">
								{totalExecutionTime}ms
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{((totalExecutionTime / totalFlowDuration) * 100).toFixed(1)}% of
								total
							</Typography>
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} sm={6} md={3}>
					<Card elevation={2}>
						<CardContent>
							<Stack direction="row" spacing={1} alignItems="center" mb={1}>
								<TrendingUp color="warning" />
								<Typography variant="body2" color="text.secondary">
									Wait Time (Gaps)
								</Typography>
							</Stack>
							<Typography variant="h5" fontWeight="bold">
								{totalGapTime}ms
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{((totalGapTime / totalFlowDuration) * 100).toFixed(1)}% of total
							</Typography>
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} sm={6} md={3}>
					<Card elevation={2}>
						<CardContent>
							<Stack direction="row" spacing={1} alignItems="center" mb={1}>
								{successRate === 100 ? (
									<CheckCircle color="success" />
								) : (
									<ErrorIcon color="error" />
								)}
								<Typography variant="body2" color="text.secondary">
									Success Rate
								</Typography>
							</Stack>
							<Typography variant="h5" fontWeight="bold">
								{successRate.toFixed(0)}%
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{requestsWithResults.filter((r) => r.result.ok).length} of{' '}
								{requestsWithResults.length} requests
							</Typography>
						</CardContent>
					</Card>
				</Grid>

				<Grid item xs={12} sm={6} md={3}>
					<Card elevation={2} sx={{ backgroundColor: 'warning.lighter', height: '100%' }}>
						<CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
							<Stack direction="row" spacing={1} alignItems="center" mb={1}>
								<TrendingUp color="warning" />
								<Typography variant="body2" color="text.secondary">
									Slowest Request
								</Typography>
							</Stack>
							<Typography
								variant="body2"
								sx={{
									fontFamily: 'monospace',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
									mb: 1,
								}}
								title={slowestRequest.request.parameterizedUrl}
							>
								{slowestRequest.request.parameterizedUrl}
							</Typography>
							<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
								{renderMethodOrType(slowestRequest.request.method, true)}
								<Chip
									label={`${slowestRequest.result.durationMs}ms`}
									color="warning"
									size="small"
									sx={{ fontWeight: 'bold' }}
								/>
								<Chip
									label={`${(
										(slowestRequest.result.durationMs / totalExecutionTime) *
										100
									).toFixed(1)}%`}
									variant="outlined"
									size="small"
								/>
							</Stack>
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			<Divider sx={{ my: 3 }} />

			{/* Timeline Chart */}
			<Card elevation={2}>
				<CardContent>
					<Typography variant="h6" fontWeight="bold" gutterBottom>
						Request Execution Timeline
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Waterfall view showing when each request was executed and how long it took.
						Hover over bars for detailed timing information.
					</Typography>
					{chartData && (
						<Box sx={{ mt: 2 }}>
							<Chart
								options={chartData.options}
								series={chartData.series}
								type="rangeBar"
								height={chartData.options.chart?.height}
							/>
						</Box>
					)}
				</CardContent>
			</Card>

			<Divider sx={{ my: 3 }} />

			{/* Detailed Request Breakdown */}
			<Card elevation={2}>
				<CardContent>
					<Typography variant="h6" fontWeight="bold" gutterBottom>
						Detailed Breakdown
					</Typography>
					<Stack spacing={2} sx={{ mt: 2 }}>
						{requestsWithResults.map((item, idx) => {
							const relativeStart = item.startTime - timelineData.flowStartTime;
							const gap = idx > 0 ? gaps[idx - 1] : 0;

							return (
								<Box key={item.request.uniqueId}>
									{gap > 0 && (
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												py: 1,
												px: 2,
												backgroundColor: 'action.hover',
												borderRadius: 1,
												mb: 1,
											}}
										>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ fontFamily: 'monospace' }}
											>
												⏸ {gap}ms gap
											</Typography>
										</Box>
									)}
									<Box
										sx={{
											p: 2,
											border: '1px solid',
											borderColor: item.result.ok
												? 'success.light'
												: 'error.light',
											borderRadius: 1,
											backgroundColor: item.result.ok
												? 'rgba(76, 175, 80, 0.04)'
												: 'rgba(244, 67, 54, 0.04)',
										}}
									>
										<Stack spacing={1}>
											<Stack
												direction="row"
												spacing={2}
												alignItems="center"
												flexWrap="wrap"
											>
												<Typography
													variant="body2"
													sx={{ fontWeight: 'bold', minWidth: 30 }}
												>
													#{idx + 1}
												</Typography>
												{renderMethodOrType(item.request.method, true)}
												<Typography
													variant="body2"
													sx={{
														fontFamily: 'monospace',
														flex: 1,
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													}}
												>
													{item.request.parameterizedUrl}
												</Typography>
												<Chip
													size="small"
													label={item.result.status || 'N/A'}
													color={item.result.ok ? 'success' : 'error'}
												/>
											</Stack>
											<Stack
												direction="row"
												spacing={3}
												sx={{
													fontSize: '0.75rem',
													color: 'text.secondary',
												}}
											>
												<Box>
													<strong>Start:</strong> T+{relativeStart}ms
												</Box>
												<Box>
													<strong>Duration:</strong>{' '}
													{item.result.durationMs}ms
												</Box>
												<Box>
													<strong>End:</strong> T+
													{relativeStart + item.result.durationMs}ms
												</Box>
												<Box>
													<strong>% of Execution:</strong>{' '}
													{(
														(item.result.durationMs /
															totalExecutionTime) *
														100
													).toFixed(1)}
													%
												</Box>
											</Stack>
										</Stack>
									</Box>
								</Box>
							);
						})}
					</Stack>
				</CardContent>
			</Card>

			{/* Legend */}
			<Box sx={{ mt: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
				<Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
					Legend
				</Typography>
				<Stack direction="row" spacing={3} flexWrap="wrap">
					<Stack direction="row" spacing={1} alignItems="center">
						<Box
							sx={{
								width: 16,
								height: 16,
								backgroundColor: '#4caf50',
								borderRadius: 0.5,
							}}
						/>
						<Typography variant="caption">Successful Request</Typography>
					</Stack>
					<Stack direction="row" spacing={1} alignItems="center">
						<Box
							sx={{
								width: 16,
								height: 16,
								backgroundColor: '#f44336',
								borderRadius: 0.5,
							}}
						/>
						<Typography variant="caption">Failed Request</Typography>
					</Stack>
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography variant="caption">⏸</Typography>
						<Typography variant="caption">Wait Time / Gap</Typography>
					</Stack>
				</Stack>
			</Box>
		</Box>
	);
};

// Helper function to shorten URLs for display
function getShortUrl(url: string): string {
	try {
		const urlObj = new URL(url);
		const pathname = urlObj.pathname;
		// Get last 2 path segments for brevity
		const segments = pathname.split('/').filter(Boolean);
		if (segments.length > 2) {
			return '.../' + segments.slice(-2).join('/');
		}
		return pathname || '/';
	} catch {
		// If URL parsing fails, return last 40 chars
		return url.length > 40 ? '...' + url.slice(-37) : url;
	}
}

export default TimelineTab;
